import { useState, useEffect, useCallback, useMemo } from "react";
import debounce from "lodash.debounce";
import { useAuth } from "../../../../utils/Auth";
import QrConnectRows from "./QrConnectRows";
import { checkUrlValidity } from "../../../../lib/checkUrlValidity";
import QrConnectHeader from "./QrConnectHeader";
import QrConnectPreview from "./QrConnectPreview";
import styles from "./QrConnect.module.css";

const DEFAULT_HEADER = { text: "", hide_text: false, color: "#000000", background: "#ffffff", image: '', use_image: false };
const DEFAULT_BUTTONS = [
  { id: "continue-1", color: "#000000", text: "Continue Browsing", background: "#ffffff", type: "continue", value: "", icon: "fa-solid fa-caret-right", border: { style: 'none', color: '#000000', width: 1, radius: 8 }, box_shadow: { x: 0, y: 1, blur: 3, color: '#dddddd'} },
  { id: "share-1", color: "#000000", text: "Share", background: "#ffffff", type: "share", value: "", icon: "fa-solid fa-share-nodes", border: { style: 'none', color: '#000000', width: 1, radius: 8 }, box_shadow: { x: 0, y: 1, blur: 3, color: '#dddddd'} }
];

export default function QrConnect() {
  const { qr_data, dataDispatch } = useAuth();
  const [ defaultUrlError, setDefaultUrlError ] = useState(false);

  // Initialize connect data if it doesn't exist
  useEffect(() => {
    if (!qr_data?.data?.connect) {
      dataDispatch({
        type: 'connect',
        data: {
          defaultUrl: '',
          buttons: DEFAULT_BUTTONS,
          header: DEFAULT_HEADER
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get current connect data from global state with defaults
  const connectData = useMemo(() => {
    const connect = qr_data?.data?.connect || {};
    return {
      defaultUrl: connect.defaultUrl || '',
      buttons: connect.buttons?.length ? connect.buttons : DEFAULT_BUTTONS,
      header: { ...DEFAULT_HEADER, ...connect.header }
    };
  }, [qr_data?.data?.connect]);

  // dispatch helper - just send partial updates, reducer handles merging
  const updateConnect = useCallback((updates) => {
    dataDispatch({ type: 'connect', data: updates });
  }, [dataDispatch]);

  const debouncedValidate = useCallback(debounce((val) => setDefaultUrlError(!!(val && !checkUrlValidity(val))), 1000), []);
  
  const formatUrl = (url) => {
    // If empty or just whitespace, return empty string
    if (!url || url.trim() === '') {
      return '';
    }
    
    // If only contains protocol with nothing after it, return empty string
    if (url.trim() === 'https://' || url.trim() === 'http://') {
      return '';
    }
    
    // Check if user is actively typing a protocol (valid prefix check)
    const https = 'https://';
    const http = 'http://';
    const lowerUrl = url.toLowerCase();
    
    // If the input is a valid prefix of either protocol, don't modify it
    if (https.startsWith(lowerUrl) || http.startsWith(lowerUrl)) {
      return url;
    }
    
    // If already has a complete protocol, return as-is
    if (url.startsWith('https://') || url.startsWith('http://')) {
      return url;
    }
    
    // Otherwise, prepend https://
    return `https://${url}`;
  };
  
  const handleUrlChange = (e) => {
    const formattedUrl = formatUrl(e.target.value);
    updateConnect({ defaultUrl: formattedUrl });
    debouncedValidate(formattedUrl);
  };

  return (
    <div className={styles.container}>
      {/* URL Input */}
      <div className={styles.inputGroup} style={{ marginBottom: "1.25rem" }}>
        <input
          type="text"
          className={styles.textInput}
          value={connectData.defaultUrl}
          onChange={handleUrlChange}
          style={{ borderColor: defaultUrlError ? "red" : undefined }}
        />
        <label className={styles.label}>Fallback URL</label>
      </div>
      <div className={styles.content}>
        <div className={styles.left}>
          <QrConnectHeader header={connectData.header} onHeaderChange={(header) => updateConnect({ header })} />
          <QrConnectRows />
        </div>
        <div className={styles.right}>
          <QrConnectPreview state={connectData} />
        </div>
      </div>
    </div>
  );
};